import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wand2, GitCompare, Ruler, Headphones, Truck, ShieldCheck, Store, Lock, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { CinematicHero } from "../components/CinematicHero";
import { ProductCard } from "../components/ProductCard";
import { RoomVisual } from "../components/RoomVisual";
import { SectionHeading, Reveal } from "../components/Section";
import { getIcon } from "../lib/icons";
import { getProducts, getCategories, getBundles, formatEUR } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

export default function Home() {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    getProducts({ sort: "featured", lang }).then((d) => setProducts(d.products.slice(0, 8))).catch(() => {});
    getCategories().then((d) => setCategories(d.categories.filter((c) => c.slug !== "accessories"))).catch(() => {});
    getBundles().then((d) => setBundles(d.bundles.slice(0, 3))).catch(() => {});
  }, [lang]);

  return (
    <div>
      <CinematicHero />

      {/* C \u2014 Category discovery */}
      <section className="section-pad container-lumeo">
        <SectionHeading eyebrow={t("nav.categories")} title={t("categoriesTiles.title")} titleMuted={t("categoriesTiles.titleMuted")} subtitle={t("categoriesTiles.subtitle")} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {categories.map((c, i) => {
            const Icon = getIcon(c.icon);
            return (
              <Reveal key={c.slug} delay={i * 0.05}>
                <Link to={`/category/${c.slug}`} data-testid={`home-category-${c.slug}`}
                  className="group relative block rounded-2xl card-ceramic p-5 h-full hover:shadow-glow-cyan transition-shadow overflow-hidden">
                  <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-30 blur-2xl" style={{ background: "radial-gradient(circle, rgba(86,227,255,0.4), transparent 70%)" }} />
                  <Icon className="h-6 w-6 text-cyanx" />
                  <h3 className="mt-4 font-display text-lg">{lang === "hr" ? c.name_hr : c.name_en}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{lang === "hr" ? c.tagline_hr : c.tagline_en}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-mono text-xs text-muted-foreground">{c.count} {t("common.results")}</span>
                    <ArrowRight className="h-4 w-4 text-cyanx opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* D \u2014 Find My Projector */}
      <section className="section-pad">
        <div className="container-lumeo">
          <div className="relative rounded-3xl card-ceramic overflow-hidden p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
            <div className="absolute inset-0 opacity-[0.05] noise-overlay" />
            <div className="absolute -left-10 top-1/2 -translate-y-1/2 h-64 w-64 rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(86,227,255,0.3), transparent 70%)" }} />
            <Reveal className="relative">
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyanx/80">{t("findSection.eyebrow")}</span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight"><span>{t("findSection.title")}</span> <span className="text-glow-cyan">{t("findSection.titleMuted")}</span></h2>
              <p className="mt-4 text-muted-foreground">{t("findSection.body")}</p>
              <Button asChild size="lg" className="mt-6 btn-glow border-0 h-12"><Link to="/advisor" data-testid="home-advisor-cta"><Wand2 className="h-5 w-5 mr-2" />{t("findSection.cta")}</Link></Button>
            </Reveal>
            <Reveal delay={0.1} className="relative">
              <div className="rounded-2xl hairline bg-[#0b0d12] p-4 space-y-3">
                {[t("advisor.bestMatch"), t("advisor.bestValue"), t("advisor.premium"), t("advisor.portable")].map((r, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl p-3 card-ceramic">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyanx/10 text-cyanx font-mono text-sm">{i + 1}</span>
                    <div className="h-2 rounded-full bg-white/10 flex-1" style={{ maxWidth: `${90 - i * 12}%` }} />
                    <span className="font-mono text-xs text-muted-foreground">{r}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* E \u2014 Simulator preview */}
      <section className="section-pad container-lumeo">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="rounded-2xl card-ceramic p-4"><RoomVisual distanceM={2.6} screenInch={100} /></div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyanx/80">{t("simSection.eyebrow")}</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight"><span>{t("simSection.title")}</span> <span className="text-muted-foreground">{t("simSection.titleMuted")}</span></h2>
            <p className="mt-4 text-muted-foreground">{t("simSection.body")}</p>
            <Button asChild size="lg" variant="secondary" className="mt-6 h-12 hairline"><Link to="/simulator" data-testid="home-simulator-cta"><Ruler className="h-5 w-5 mr-2 text-cyanx" />{t("simSection.cta")}</Link></Button>
          </Reveal>
        </div>
      </section>

      {/* F \u2014 Best sellers */}
      <section className="section-pad container-lumeo">
        <SectionHeading eyebrow={t("nav.shop")} title={t("bestSellers.title")} titleMuted={t("bestSellers.titleMuted")} subtitle={t("bestSellers.subtitle")} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
        <div className="text-center mt-10"><Button asChild variant="secondary" className="hairline h-11"><Link to="/shop">{t("common.viewAll")} <ArrowRight className="h-4 w-4 ml-1" /></Link></Button></div>
      </section>

      {/* G \u2014 Compare promo */}
      <section className="section-pad">
        <div className="container-lumeo">
          <Reveal className="relative rounded-3xl overflow-hidden p-8 sm:p-12 text-center card-ceramic">
            <div className="absolute inset-0" style={{ background: "radial-gradient(700px 300px at 50% 0%, rgba(212,168,67,0.10), transparent 70%)" }} />
            <GitCompare className="h-8 w-8 text-amberx mx-auto" />
            <h2 className="mt-4 font-display text-3xl sm:text-4xl"><span>{t("compareSection.title")}</span> <span className="text-glow-amber">{t("compareSection.titleMuted")}</span></h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">{t("compareSection.body")}</p>
            <Button asChild size="lg" className="mt-6 btn-glow border-0 h-12"><Link to="/compare">{t("compareSection.cta")}</Link></Button>
          </Reveal>
        </div>
      </section>

      {/* H \u2014 Bundles */}
      <section className="section-pad container-lumeo">
        <SectionHeading eyebrow={t("nav.bundles")} title={t("bundlesSection.title")} titleMuted={t("bundlesSection.titleMuted")} subtitle={t("bundlesSection.subtitle")} />
        <div className="grid md:grid-cols-3 gap-4">
          {bundles.map((b, i) => {
            const Icon = getIcon(b.icon);
            return (
              <Reveal key={b.slug} delay={i * 0.05}>
                <Link to="/bundles" className="group block rounded-2xl card-ceramic p-6 h-full hover:shadow-glow-cyan transition-shadow">
                  <div className="flex items-center justify-between">
                    <Icon className="h-6 w-6 text-cyanx" />
                    <span className="chip text-mintx">{t("common.save")} {formatEUR(b.savings_eur)}</span>
                  </div>
                  <h3 className="mt-4 font-display text-lg">{lang === "hr" ? b.name_hr : b.name_en}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{lang === "hr" ? b.tagline_hr : b.tagline_en}</p>
                  <div className="mt-4 flex items-center gap-2">
                    <span className="font-display text-xl">{formatEUR(b.bundle_price_eur)}</span>
                    <span className="text-xs text-muted-foreground line-through">{formatEUR(b.total_eur)}</span>
                  </div>
                  <div className="mt-3 flex -space-x-2">
                    {b.items.filter((it) => it.image).slice(0, 3).map((it) => (
                      <span key={it.slug} className="h-9 w-9 rounded-lg bg-black/50 hairline overflow-hidden"><img src={it.image} alt="" className="h-full w-full object-contain p-0.5" /></span>
                    ))}
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* I \u2014 Trust */}
      <section className="section-pad container-lumeo">
        <SectionHeading title={t("trust.title")} center />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[[Headphones, "expert"], [Truck, "delivery"], [Lock, "secure"], [ShieldCheck, "warranty"], [Store, "wholesale"]].map(([Icon, key], i) => (
            <Reveal key={key} delay={i * 0.05}>
              <div className="rounded-2xl card-ceramic p-5 h-full">
                <Icon className="h-6 w-6 text-cyanx" />
                <h3 className="mt-3 font-medium">{t(`trust.${key}`)}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{t(`trust.${key}Body`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
